import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  MoreVertical,
  MailMinus,
  CheckCircle2,
  RotateCcw,
  Trash2,
  ArchiveRestore,
  Star,
  Tags,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { ConfirmDialog } from './ui';
import { TagPopover } from './TagPopover';
import type { ConversationTag, Status } from '../types';

/**
 * 3-Punkte-Aktionsmenü einer Konversation. Öffnet ein an den Trigger angedocktes
 * Popover (Portal — entkommt overflow:hidden/Stacking der Liste). Esc + Klick-außen
 * schließen, der Fokus kehrt beim Schließen zum Trigger zurück.
 *
 * Aktionen sind kontextabhängig (Status / Papierkorb / Wichtig). Alle laufen als
 * TanStack-Mutations mit Invalidierung der Listen- und Detail-Queries; Erfolg/Fehler
 * über sonner-Toasts.
 */

interface ConversationLike {
  id: string;
  status: Status;
  isImportant: boolean;
  deletedAt: string | null;
  channelAccount: { id: string };
  tags: ConversationTag[];
}

interface Props {
  conversation: ConversationLike;
  /** „compact" für Listen-Items (kleiner Button), „header" für die Detail-Kopfzeile. */
  variant?: 'compact' | 'header';
  className?: string;
}

export function ConversationActionsMenu({ conversation, variant = 'compact', className = '' }: Props) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [confirmTrash, setConfirmTrash] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const { id, status, isImportant, deletedAt } = conversation;

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
    queryClient.invalidateQueries({ queryKey: ['conversation', id] });
    // Tab-Titel-Zähler: unread/close/reopen/trash/restore ändern ihn unmittelbar.
    queryClient.invalidateQueries({ queryKey: ['unread-count'] });
  }

  const action = useMutation({
    mutationFn: (input: { path: string; body?: unknown }) =>
      api.post(`/api/conversations/${id}/${input.path}`, input.body ?? {}),
    onSuccess: (_data, input) => {
      invalidate();
      if (input.path === 'important') {
        const value = (input.body as { value?: boolean } | undefined)?.value;
        toast.success(value ? 'Als wichtig markiert.' : 'Wichtig-Markierung entfernt.');
      } else {
        toast.success(SUCCESS_MESSAGES[input.path] ?? 'Erledigt.');
      }
    },
    onError: () => toast.error('Aktion fehlgeschlagen.'),
  });

  function run(path: string, body?: unknown) {
    setOpen(false);
    action.mutate({ path, body });
  }

  // Popover relativ zum Trigger positionieren (rechtsbündig), nach dem Öffnen messen.
  useLayoutEffect(() => {
    if (!open || tagsOpen) return;
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = 224; // w-56
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    setPos({ top: rect.bottom + 4, left });
  }, [open, tagsOpen]);

  // Esc + Klick-außen schließen das Popover; Fokus zurück auf den Trigger.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
      }
    }
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (popoverRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      close();
    }
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  function close() {
    setOpen(false);
    setTagsOpen(false);
    triggerRef.current?.focus();
  }

  const triggerClass =
    variant === 'header'
      ? 'rounded-pill p-1.5 text-kalkuel-muted hover:bg-kalkuel-surface hover:text-kalkuel-primary'
      : 'rounded-pill p-1 text-kalkuel-muted hover:bg-white hover:text-kalkuel-primary';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Aktionen"
        title="Aktionen"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setTagsOpen(false);
          setOpen((o) => !o);
        }}
        className={[
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40',
          triggerClass,
          className,
        ].join(' ')}
      >
        <MoreVertical size={variant === 'header' ? 16 : 15} />
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={popoverRef}
            role={tagsOpen ? undefined : 'menu'}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
            className="z-[75] w-56 overflow-hidden rounded-md border border-kalkuel-border bg-white py-1 shadow-brand-lg"
          >
            {tagsOpen ? (
              <TagPopover
                conversation={{ id, channelAccount: conversation.channelAccount }}
                tags={conversation.tags}
                onClose={() => setTagsOpen(false)}
              />
            ) : (
              <>
                <MenuItem icon={<MailMinus size={14} />} onClick={() => run('unread')}>
                  Als ungelesen markieren
                </MenuItem>

                {status !== 'CLOSED' ? (
                  <MenuItem icon={<CheckCircle2 size={14} />} onClick={() => run('close')}>
                    Als erledigt markieren
                  </MenuItem>
                ) : (
                  <MenuItem icon={<RotateCcw size={14} />} onClick={() => run('reopen')}>
                    Wiedereröffnen
                  </MenuItem>
                )}

                <MenuItem
                  icon={<Star size={14} className={isImportant ? 'fill-amber-400 text-amber-400' : ''} />}
                  onClick={() => run('important', { value: !isImportant })}
                >
                  {isImportant ? 'Wichtig-Markierung entfernen' : 'Als wichtig markieren'}
                </MenuItem>

                <MenuItem
                  icon={<Tags size={14} />}
                  onClick={() => setTagsOpen(true)}
                  hasSubmenu
                >
                  Tags anpassen…
                </MenuItem>

                <div className="my-1 border-t border-kalkuel-border" />

                {!deletedAt ? (
                  <MenuItem
                    icon={<Trash2 size={14} />}
                    danger
                    onClick={() => {
                      setOpen(false);
                      setConfirmTrash(true);
                    }}
                  >
                    In den Papierkorb
                  </MenuItem>
                ) : (
                  <MenuItem icon={<ArchiveRestore size={14} />} onClick={() => run('restore')}>
                    Wiederherstellen
                  </MenuItem>
                )}
              </>
            )}
          </div>,
          document.body,
        )}

      <ConfirmDialog
        open={confirmTrash}
        danger
        title="In den Papierkorb"
        message="Diese Konversation in den Papierkorb verschieben? Sie lässt sich später wiederherstellen."
        confirmLabel="In den Papierkorb"
        onConfirm={() => {
          setConfirmTrash(false);
          action.mutate({ path: 'trash' });
        }}
        onClose={() => setConfirmTrash(false)}
      />
    </>
  );
}

const SUCCESS_MESSAGES: Record<string, string> = {
  unread: 'Als ungelesen markiert.',
  close: 'Als erledigt markiert.',
  reopen: 'Wiedereröffnet.',
  trash: 'In den Papierkorb verschoben.',
  restore: 'Wiederhergestellt.',
};

function MenuItem({
  icon,
  children,
  onClick,
  danger = false,
  hasSubmenu = false,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  hasSubmenu?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={[
        'flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs font-medium transition-colors focus:outline-none',
        danger
          ? 'text-red-600 hover:bg-red-50 focus-visible:bg-red-50'
          : 'text-kalkuel-primary hover:bg-kalkuel-surface focus-visible:bg-kalkuel-surface',
      ].join(' ')}
    >
      <span className="shrink-0 text-kalkuel-muted">{icon}</span>
      <span className="flex-1 truncate">{children}</span>
      {hasSubmenu && <span className="text-kalkuel-muted">›</span>}
    </button>
  );
}
